import type { RenderStateColorGradient, RGBA } from "@novorender/api";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useLazyGetDeviationProfilesQuery } from "apis/dataV2/dataV2Api";
import type { ColorStop, DeviationProjectConfig } from "apis/dataV2/deviationTypes";
import { Permission } from "apis/dataV2/permissions";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectAlignmentIds } from "features/alignment/selectors";
import { loadCommittedDeviations } from "features/offline/hostData/offlineHostData";
import { renderActions, selectPoints, selectViewMode } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { useCheckProjectPermission } from "hooks/useCheckProjectPermissions";
import { useSceneId } from "hooks/useSceneId";
import { selectIsOnline, selectProjectIsV2 } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncStatus, ViewMode } from "types/misc";
import { getAssetUrl } from "utils/misc";

import { deviationsActions } from "./deviationsSlice";
import type { UiDeviationConfig } from "./deviationTypes";
import { useHighlightDeviation } from "./hooks/useHighlightDeviation";
import { useSetCenterLineAlignment } from "./hooks/useSetCenterLineAlignment";
import { useSyncWithAlignment } from "./hooks/useSyncWithAlignment";
import {
    selectDeviationProfiles,
    selectNumPointDeviations,
    selectSelectedProfile,
    selectSelectedProfileId,
} from "./selectors";
import {
    accountForAbsValues,
    countCenterLines,
    MAX_DEVIATION_PROFILE_COUNT,
    reconcileCenterlines,
    serverConfigToUiConfig,
} from "./utils";

const EMPTY_ARRAY: ColorStop[] = [];

export function useHandleDeviations() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const { t } = useTranslation();
    const isProjectV2 = useAppSelector(selectProjectIsV2);
    const isOnline = useAppSelector(selectIsOnline);
    const sceneId = useSceneId();
    const numPointDeviations = useAppSelector(selectNumPointDeviations);
    const profiles = useAppSelector(selectDeviationProfiles);
    const selectedProfileId = useAppSelector(selectSelectedProfileId);
    const dispatch = useAppDispatch();
    const points = useAppSelector(selectPoints);
    const defaultColorStops = points.deviation.colorGradient.knots ?? EMPTY_ARRAY;
    const profile = useAppSelector(selectSelectedProfile);
    const [abortController] = useAbortController();
    const active = useAppSelector(selectViewMode) === ViewMode.Deviations;
    const alignmentIds = useAppSelector(selectAlignmentIds);
    const checkProjectPermission = useCheckProjectPermission();

    const [getDeviationProfiles] = useLazyGetDeviationProfilesQuery();

    useSetCenterLineAlignment();
    useHighlightDeviation();
    useSyncWithAlignment();

    useEffect(() => {
        initDeviationProfiles();

        async function initDeviationProfiles() {
            if (
                !view ||
                !sceneId ||
                profiles.status !== AsyncStatus.Initial ||
                alignmentIds.status !== AsyncStatus.Success ||
                !checkProjectPermission(Permission.DeviationRead) ||
                numPointDeviations === undefined
            ) {
                return;
            }

            dispatch(deviationsActions.setProfiles({ status: AsyncStatus.Loading }));

            try {
                const url = (await getAssetUrl(view, "deviations.json")).toString();

                // When we run the calculation - saved config is copied to another storage where calculator picks it up, so
                // savedConfig - the one where we save changes
                // commitedConfig - the one picked up (and later updated!) by the service
                const [commitedConfig, savedConfig] = await Promise.all([
                    numPointDeviations > 0 ? loadCommittedDeviations({ url, projectId: sceneId, isOnline }) : undefined,
                    isProjectV2
                        ? getDeviationProfiles({ projectId: sceneId })
                              .unwrap()
                              .then((data) => (data ? serverConfigToUiConfig(data, defaultColorStops) : undefined))
                        : undefined,
                ]);

                if (commitedConfig && savedConfig) {
                    // Commited config always has relevant runData
                    savedConfig.runData = commitedConfig.runData;
                }

                let config: UiDeviationConfig;
                if (commitedConfig && savedConfig) {
                    config = savedConfig;
                    matchProfileIndexes(savedConfig, commitedConfig);
                } else if (commitedConfig) {
                    config = serverConfigToUiConfig(commitedConfig, defaultColorStops);
                    config.profiles.forEach((p, i) => (p.index = i));
                } else if (savedConfig) {
                    config = savedConfig;
                } else {
                    config = getEmptyDeviationConfig();
                }
                const centerLines = countCenterLines(config);
                if (centerLines.total > 0) {
                    telemetry.trackEvent("deviations.center_lines_loaded", {
                        "deviation.center_line_count": centerLines.total,
                        "deviation.center_line_without_instance_id_count": centerLines.withoutInstanceId,
                    });
                }

                config = reconcileCenterlines(config, alignmentIds.data);

                dispatch(
                    deviationsActions.setProfiles({
                        status: AsyncStatus.Success,
                        data: config,
                    }),
                );

                dispatch(
                    deviationsActions.initFromProfileIndex({
                        index: points.deviation.index,
                    }),
                );
            } catch (e) {
                console.warn(e);
                telemetry.trackCaughtException({
                    source: "deviations.useHandleDeviations.initDeviationProfiles",
                    category: "data",
                    error: e,
                });
                dispatch(
                    deviationsActions.setProfiles({
                        status: AsyncStatus.Error,
                        msg: t("loadingDeviationsError"),
                    }),
                );
            }
        }
    }, [
        dispatch,
        view,
        isProjectV2,
        sceneId,
        profiles,
        defaultColorStops,
        getDeviationProfiles,
        points.deviation.index,
        abortController,
        selectedProfileId,
        alignmentIds,
        checkProjectPermission,
        t,
        numPointDeviations,
        isOnline,
    ]);

    // Set current deviation and colors
    useEffect(() => {
        if (profile && active) {
            let colorStops = profile.colors.colorStops.slice();
            if (profile.colors.absoluteValues) {
                colorStops = accountForAbsValues(colorStops);
            }
            dispatch(
                renderActions.setPoints({
                    deviation: {
                        index: profile.index,
                        colorGradient: {
                            knots: colorStops,
                        },
                    },
                }),
            );
        }
    }, [dispatch, profile, active]);

    // Set deviation colors
    useEffect(() => {
        if (profiles.status !== AsyncStatus.Success || !view) {
            return;
        }

        const colorGradients: RenderStateColorGradient<RGBA>[] = new Array(MAX_DEVIATION_PROFILE_COUNT).fill({
            knots: [],
        } as RenderStateColorGradient<RGBA>);

        profiles.data.profiles.forEach((profile, index) => {
            if (index < 0 || index >= colorGradients.length) {
                return;
            }

            let knots = profile.colors.colorStops.slice();
            if (profile.colors.absoluteValues) {
                knots = accountForAbsValues(knots);
            }

            colorGradients[profile.index] = {
                knots: knots.toSorted((a, b) => a.position - b.position),
            };
        });

        view.modifyRenderState({
            points: { deviation: { colorGradients } },
        });
    }, [view, profiles]);
}

function getEmptyDeviationConfig(): UiDeviationConfig {
    return {
        version: "1.0",
        rebuildRequired: false,
        profiles: [],
    };
}

function matchProfileIndexes(current: UiDeviationConfig, calculated: DeviationProjectConfig) {
    const calcProfiles = [
        ...calculated.pointToTriangle.groups.map((g) => ({
            id: g.id,
            name: g.name,
        })),
        ...calculated.pointToPoint.groups.map((g) => ({
            id: g.id,
            name: g.name,
        })),
    ];

    const calculatedHasIds = calcProfiles.some((p) => p.id);

    current.profiles.forEach((profile, i) => {
        if (calculatedHasIds) {
            profile.index = calcProfiles.findIndex((p) => p.id === profile.id);
        } else {
            profile.index = i;
        }
    });
}
